import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ChevronDown, ChevronRight,
  MapPin, Map as MapIcon, Building2, Car,
  Store, Mountain, Tag, Box,
  Signpost, Grid3X3, Footprints, Ship, Home, Info, Construction, TreePine, CloudRain, CloudSun,
  Thermometer, Wind, Droplets, Compass,
} from 'lucide-react';
import { FLOOD_EXPOSURE_GRID_LEGEND, FLOOD_EXPOSURE_SUMMARY, FLOOD_LAYER_MAX_ZOOM, FLOOD_TILE_MIN_ZOOM } from '../../lib/floodExposure';
import {
  fetchTiledPeriodManifest,
  isFloodYearPeriod,
  periodKindPillLabel,
} from '../../lib/tilePeriods';
import {
  OBSERVED_WEATHER_UI_DEFS,
  OBSERVED_WEATHER_LAYER_MAX_ZOOM,
  OBSERVED_WEATHER_TILE_MIN_ZOOM,
  formatObservedWeatherMonthPill,
  latestTiledPeriod,
  type ObservedWeatherLayerDef,
  type ObservedWeatherPeriodKey,
} from '../../lib/observedWeather';
import {
  FORECAST_WEATHER_LAYER_DEFS,
  FORECAST_LAYER_MAX_ZOOM,
  FORECAST_TILE_MIN_ZOOM,
  formatForecastDayPill,
  latestTiledForecastPeriod,
  type ForecastWeatherLayerDef,
  type ForecastWeatherPeriodKey,
} from '../../lib/forecastWeather';
import { HierarchicalPeriodPicker } from './HierarchicalPeriodPicker';
import { WeatherConditionsWarning } from '../weather/WeatherConditionsWarning';
import { ChoroplethColorScale } from './ChoroplethColorScale';
import { CategoricalLegendPill } from './CategoricalLegendPill';

/** Top-level layer groups — icons jump-scroll the panel. */
const LAYER_SECTION_NAV = [
  { id: 'terrain', label: 'Terrain', icon: Mountain },
  { id: 'admin', label: 'Boundaries', icon: MapIcon },
  { id: 'land', label: 'Land', icon: TreePine },
  { id: 'weather', label: 'Weather', icon: CloudRain },
  { id: 'forecast', label: 'Forecast', icon: CloudSun },
  { id: 'roads', label: 'Roads', icon: Car },
  { id: 'buildings', label: 'Buildings', icon: Building2 },
  { id: 'places', label: 'Places', icon: MapPin },
  { id: 'poi', label: 'POI', icon: Store },
] as const;

type LayerSectionId = (typeof LAYER_SECTION_NAV)[number]['id'];
export type { LayerSectionId };

const DEFAULT_OPEN_SECTIONS: Record<LayerSectionId, boolean> = {
  terrain: false,
  admin: false,
  land: false,
  weather: false,
  forecast: false,
  roads: false,
  buildings: false,
  places: false,
  poi: false,
};

// Unified layer settings
// Each key maps to a group in LAYER_IDS (mapConfig.ts).
export interface LayerSettings {
  // Admin boundaries
  country: boolean;
  regions: boolean;
  districts: boolean;
  // Service areas (grid overlay)
  serviceAreas: boolean;
  // Buildings
  buildings: boolean;
  buildings3D: boolean;
  buildingLabels: boolean;
  // Roads - combined and granular controls
  roads: boolean;
  motorways: boolean;
  primaryRoads: boolean;
  secondaryRoads: boolean;
  minorRoads: boolean;
  pedestrianPaths: boolean;
  tunnels: boolean;
  bridges: boolean;
  roadLabels: boolean;
  // Transit
  ferryRoutes: boolean;
  transportStops: boolean;
  trafficSignals: boolean;
  /** Live congestion lines from Coordal traffic_edge (Valhalla edge.id aligned). */
  liveTraffic: boolean;
  roadQuality: boolean;
  // Labels
  placeLabels: boolean;
  poiLabels: boolean;
  settlementLabels: boolean;
  // Terrain
  terrain: boolean;
  hillshade: boolean;
  contours: boolean;
  // Land cover / use (Coordal tiles)
  landcover: boolean;
  landuse: boolean;
  nationalParks: boolean;
  water: boolean;
  waterways: boolean;
  structures: boolean;
  trees: boolean;
  floodExposure: boolean;
  /** Selected calendar years for yearly flood exposure grid overlay. */
  floodExposureYears: number[];
  /** Monthly observed weather overlays — each variable has its own month picker. */
  observedWeatherTemp: boolean;
  observedWeatherWind: boolean;
  observedWeatherWindDirection: boolean;
  observedWeatherPrecip: boolean;
  observedWeatherTempPeriods: string[];
  observedWeatherWindPeriods: string[];
  observedWeatherWindDirectionPeriods: string[];
  observedWeatherPrecipPeriods: string[];
  forecastWeatherTemp: boolean;
  forecastWeatherPrecip: boolean;
  forecastWeatherWind: boolean;
  forecastWeatherTempPeriods: string[];
  forecastWeatherPrecipPeriods: string[];
  forecastWeatherWindPeriods: string[];
}

export const DEFAULT_LAYER_SETTINGS: LayerSettings = {
  // Admin boundaries
  country: true,
  regions: true,
  districts: true,
  // Service areas off by default (grid overlay)
  serviceAreas: false,
  // Buildings
  buildings: true,
  buildings3D: true,
  buildingLabels: true,
  // Roads - all on by default
  roads: true,
  motorways: true,
  primaryRoads: true,
  secondaryRoads: true,
  minorRoads: true,
  pedestrianPaths: true,
  tunnels: true,
  bridges: true,
  roadLabels: true,
  // Transit
  ferryRoutes: true,
  transportStops: true,
  trafficSignals: true,
  liveTraffic: false,
  roadQuality: true,
  // Labels
  placeLabels: true,
  poiLabels: true,
  settlementLabels: true,
  // Terrain - all off by default
  terrain: false,
  hillshade: false,
  contours: false,
  // Land — on by default (yellow/green fills in urban areas)
  landcover: true,
  landuse: true,
  nationalParks: true,
  water: true,
  waterways: true,
  structures: true,
  trees: true,
  floodExposure: false,
  floodExposureYears: [],
  observedWeatherTemp: false,
  observedWeatherWind: false,
  observedWeatherWindDirection: false,
  observedWeatherPrecip: false,
  observedWeatherTempPeriods: [],
  observedWeatherWindPeriods: [],
  observedWeatherWindDirectionPeriods: [],
  observedWeatherPrecipPeriods: [],
  forecastWeatherTemp: false,
  forecastWeatherPrecip: false,
  forecastWeatherWind: false,
  forecastWeatherTempPeriods: [],
  forecastWeatherPrecipPeriods: [],
  forecastWeatherWindPeriods: [],
};

interface LayerSettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  layers: LayerSettings;
  onToggleLayer: (layer: keyof LayerSettings, value: boolean) => void;
  onToggleFloodYear?: (year: number, enabled: boolean) => void;
  onObservedWeatherPeriodChange?: (key: ObservedWeatherPeriodKey, period: string, enabled: boolean) => void;
  onForecastWeatherPeriodChange?: (key: ForecastWeatherPeriodKey, period: string, enabled: boolean) => void;
  onOpenWeatherView?: () => void;
  /** When set, past weather pattern layers require in-Ghana GPS (same as live forecast). */
  weatherLocationUnavailableMessage?: string | null;
  /** Jump-scroll to a section when the panel opens (e.g. Forecast nav shortcut). */
  initialSection?: LayerSectionId | null;
  terrainExaggeration?: number;
  onTerrainExaggerationChange?: (value: number) => void;
  isTerrainEnabled?: boolean;
  onToggleTerrain?: (enabled: boolean) => void;
}

interface LayerToggleProps {
  label: string;
  enabled: boolean;
  onToggle: () => void;
  icon?: React.ReactNode;
  color?: string;
  onColorChange?: (color: string) => void;
  zoomRange?: string; // e.g., "z10-22"
  info?: string; // Description tooltip
}

function LayerToggle({ label, enabled, onToggle, icon, color, onColorChange, zoomRange, info }: LayerToggleProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="py-1.5 px-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg group">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {icon && <span className="text-gray-500 dark:text-gray-400 flex-shrink-0">{icon}</span>}
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-gray-700 dark:text-gray-200">{label}</span>
              {info && (
                <button
                  type="button"
                  onClick={() => setShowTooltip(!showTooltip)}
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex-shrink-0 -my-1"
                  aria-label={`Info about ${label}`}
                >
                  <Info size={16} className="text-gray-400 dark:text-gray-500" />
                </button>
              )}
            </div>
            {zoomRange && (
              <span className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">{zoomRange}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {color && onColorChange && (
            <input
              type="color"
              value={color}
              onChange={(e) => onColorChange(e.target.value)}
              className="w-6 h-6 rounded border border-gray-200 dark:border-gray-600 cursor-pointer"
            />
          )}
          <button
            onClick={onToggle}
            className={`w-10 h-5 rounded-full transition-colors relative ${enabled ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
          >
            <div
              className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0.5'
                }`}
            />
          </button>
        </div>
      </div>
      {showTooltip && info && (
        <div className="mt-1.5 ml-6 p-2.5 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-lg leading-relaxed">
          {info}
        </div>
      )}
    </div>
  );
}

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badge?: string;
  /** Nested inside another CollapsibleSection — smaller header, no section divider. */
  nested?: boolean;
  sectionId?: LayerSectionId;
  sectionRef?: (el: HTMLDivElement | null) => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function CollapsibleSection({
  title,
  children,
  defaultOpen = true,
  badge,
  nested = false,
  sectionId,
  sectionRef,
  isOpen: controlledOpen,
  onOpenChange,
}: CollapsibleSectionProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isOpen = controlledOpen ?? internalOpen;

  const setOpen = (open: boolean) => {
    onOpenChange?.(open);
    if (controlledOpen === undefined) {
      setInternalOpen(open);
    }
  };

  return (
    <div
      ref={sectionRef}
      id={sectionId}
      className={nested ? '' : 'border-b border-gray-100 dark:border-gray-700/50 last:border-b-0 scroll-mt-36'}
    >
      <button
        type="button"
        onClick={() => setOpen(!isOpen)}
        className={`w-full flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
          nested ? 'py-2 px-2 rounded-md' : 'py-3 px-2'
        }`}
      >
        <div className="flex items-center gap-2">
          {isOpen ? <ChevronDown size={nested ? 14 : 16} className="text-gray-600 dark:text-gray-400" /> : <ChevronRight size={nested ? 14 : 16} className="text-gray-600 dark:text-gray-400" />}
          <span className={`font-medium text-gray-800 dark:text-gray-200 ${nested ? 'text-xs' : 'text-sm'}`}>{title}</span>
          {badge && (
            <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded">
              {badge}
            </span>
          )}
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className={nested ? 'pb-1 px-1' : 'pb-2 px-1'}>{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const OBSERVED_WEATHER_ICONS: Record<string, React.ReactNode> = {
  observedWeatherTemp: <Thermometer size={16} />,
  observedWeatherWind: <Wind size={16} />,
  observedWeatherWindDirection: <Compass size={16} />,
  observedWeatherPrecip: <Droplets size={16} />,
};

const FORECAST_WEATHER_ICONS: Record<string, React.ReactNode> = {
  forecastWeatherTemp: <Thermometer size={16} />,
  forecastWeatherWind: <Wind size={16} />,
  forecastWeatherPrecip: <Droplets size={16} />,
};

function ForecastWeatherVariableSection({
  def,
  enabled,
  selectedPeriods,
  onToggleLayer,
  onForecastWeatherPeriodChange,
  nested = false,
}: {
  def: ForecastWeatherLayerDef;
  enabled: boolean;
  selectedPeriods: string[];
  onToggleLayer: LayerSettingsPanelProps['onToggleLayer'];
  onForecastWeatherPeriodChange?: LayerSettingsPanelProps['onForecastWeatherPeriodChange'];
  nested?: boolean;
}) {
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);
  const [periodKind, setPeriodKind] = useState<string | undefined>();

  const ensureDefaultPeriod = (loaded: string[], isEnabled: boolean, current: string[]) => {
    if (!isEnabled || current.length > 0) return;
    const latest = latestTiledForecastPeriod(loaded);
    if (latest) onForecastWeatherPeriodChange?.(def.periodSettingsKey, latest, true);
  };

  useEffect(() => {
    let cancelled = false;
    void fetchTiledPeriodManifest(def.tileLayer).then(({ periods: loaded, periodKind: kind }) => {
      if (cancelled) return;
      setAvailablePeriods(loaded);
      setPeriodKind(kind);
      for (const period of selectedPeriods) {
        if (!loaded.includes(period)) {
          onForecastWeatherPeriodChange?.(def.periodSettingsKey, period, false);
        }
      }
      ensureDefaultPeriod(loaded, enabled, selectedPeriods);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [def.tileLayer]);

  useEffect(() => {
    ensureDefaultPeriod(availablePeriods, enabled, selectedPeriods);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, availablePeriods.length, selectedPeriods.length]);

  const handleToggleLayer = () => {
    const nextEnabled = !enabled;
    onToggleLayer(def.settingsKey, nextEnabled);
    if (nextEnabled) ensureDefaultPeriod(availablePeriods, true, selectedPeriods);
  };

  return (
    <CollapsibleSection title={def.sectionTitle} defaultOpen={false} nested={nested}>
      <p className="mx-2 mb-2 text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">{def.summary}</p>
      <LayerToggle
        label={def.toggleLabel}
        enabled={enabled}
        onToggle={handleToggleLayer}
        icon={FORECAST_WEATHER_ICONS[def.settingsKey]}
        zoomRange={`z${FORECAST_TILE_MIN_ZOOM}-${FORECAST_LAYER_MAX_ZOOM}`}
        info={def.info}
      />
      <ChoroplethColorScale variant="panel" {...def.legend} />
      <HierarchicalPeriodPicker
        label={periodKind === 'hourly' ? 'Hours' : 'Days'}
        options={availablePeriods}
        selected={selectedPeriods}
        periodKind={periodKind === 'hourly' ? 'weekly' : 'yearly'}
        onToggle={(period, active) => onForecastWeatherPeriodChange?.(def.periodSettingsKey, period, active)}
        formatOption={formatForecastDayPill}
        emptyMessage="Forecast map layers are not available yet. Check back soon."
      />
    </CollapsibleSection>
  );
}

function ObservedWeatherVariableSection({
  def,
  enabled,
  selectedPeriods,
  onToggleLayer,
  onObservedWeatherPeriodChange,
  nested = false,
}: {
  def: ObservedWeatherLayerDef;
  enabled: boolean;
  selectedPeriods: string[];
  onToggleLayer: LayerSettingsPanelProps['onToggleLayer'];
  onObservedWeatherPeriodChange?: LayerSettingsPanelProps['onObservedWeatherPeriodChange'];
  nested?: boolean;
}) {
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);
  const [periodKind, setPeriodKind] = useState<string | undefined>();
  const [periodLabel, setPeriodLabel] = useState('Periods');

  const ensureDefaultPeriod = (loaded: string[], isEnabled: boolean, current: string[]) => {
    if (!isEnabled || current.length > 0) return;
    const latest = latestTiledPeriod(loaded);
    if (latest) {
      onObservedWeatherPeriodChange?.(def.periodSettingsKey, latest, true);
    }
  };

  useEffect(() => {
    let cancelled = false;
    void fetchTiledPeriodManifest(def.tileLayer).then(({ periods: loaded, periodKind }) => {
      if (cancelled) return;
      setAvailablePeriods(loaded);
      setPeriodKind(periodKind);
      setPeriodLabel(periodKindPillLabel(periodKind));
      for (const period of selectedPeriods) {
        if (!loaded.includes(period)) {
          onObservedWeatherPeriodChange?.(def.periodSettingsKey, period, false);
        }
      }
      ensureDefaultPeriod(loaded, enabled, selectedPeriods);
    });
    return () => {
      cancelled = true;
    };
    // Only refetch when tile layer changes; prune stale selections once per fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [def.tileLayer]);

  useEffect(() => {
    ensureDefaultPeriod(availablePeriods, enabled, selectedPeriods);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, availablePeriods.length, selectedPeriods.length]);

  const handleToggleLayer = () => {
    const nextEnabled = !enabled;
    onToggleLayer(def.settingsKey, nextEnabled);
    if (nextEnabled) {
      ensureDefaultPeriod(availablePeriods, true, selectedPeriods);
    }
  };

  return (
    <CollapsibleSection title={def.sectionTitle} defaultOpen={false} nested={nested}>
      <p className="mx-2 mb-2 text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
        {def.summary}
      </p>
      <LayerToggle
        label={def.toggleLabel}
        enabled={enabled}
        onToggle={handleToggleLayer}
        icon={OBSERVED_WEATHER_ICONS[def.settingsKey]}
        zoomRange={`z${OBSERVED_WEATHER_TILE_MIN_ZOOM}-${OBSERVED_WEATHER_LAYER_MAX_ZOOM}`}
        info={def.info}
      />
      <ChoroplethColorScale variant="panel" {...def.legend} />
      {def.settingsKey === 'observedWeatherWind' && (
        <p className="mx-2 mb-2 text-[10px] text-gray-500 dark:text-gray-400 leading-relaxed">
          Direction arrows appear from z6 when a direction slice is tiled for the selected week.
        </p>
      )}
      <HierarchicalPeriodPicker
        label={periodLabel}
        options={availablePeriods}
        selected={selectedPeriods}
        periodKind={periodKind}
        onToggle={(period, active) => onObservedWeatherPeriodChange?.(def.periodSettingsKey, period, active)}
        formatOption={formatObservedWeatherMonthPill}
        emptyMessage="No time periods available yet."
      />
    </CollapsibleSection>
  );
}

function FloodExposureYearPills({
  selectedYears,
  onToggleFloodYear,
}: {
  selectedYears: number[];
  onToggleFloodYear?: (year: number, enabled: boolean) => void;
}) {
  const [availableYears, setAvailableYears] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    void fetchTiledPeriodManifest('flood_exposure').then(({ periods }) => {
      if (cancelled) return;
      const years = periods.filter(isFloodYearPeriod).sort();
      setAvailableYears(years);
      for (const year of selectedYears) {
        if (!years.includes(String(year))) {
          onToggleFloodYear?.(year, false);
        }
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <HierarchicalPeriodPicker
      label="Years (tiled)"
      options={availableYears}
      selected={selectedYears.map(String).filter((y) => availableYears.includes(y))}
      periodKind="yearly"
      onToggle={(year, active) => onToggleFloodYear?.(Number(year), active)}
      formatOption={(year) => year}
      emptyMessage="Historical flood layers are not available yet."
    />
  );
}

export function LayerSettingsPanel({
  isOpen,
  onClose,
  layers,
  onToggleLayer,
  onToggleFloodYear,
  onObservedWeatherPeriodChange,
  onForecastWeatherPeriodChange,
  onOpenWeatherView,
  initialSection = null,
  weatherLocationUnavailableMessage = null,
  terrainExaggeration = 1.5,
  onTerrainExaggerationChange,
  isTerrainEnabled = false,
  onToggleTerrain,
}: LayerSettingsPanelProps) {
  const [openSections, setOpenSections] = useState(DEFAULT_OPEN_SECTIONS);
  const sectionRefs = useRef<Partial<Record<LayerSectionId, HTMLDivElement | null>>>({});

  const jumpToSection = useCallback((id: LayerSectionId) => {
    setOpenSections((prev) => {
      const nextOpen = !prev[id];
      if (nextOpen) {
        window.setTimeout(() => {
          sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 80);
      }
      return { ...prev, [id]: nextOpen };
    });
  }, []);

  const bindSection = (id: LayerSectionId) => ({
    sectionId: id,
    sectionRef: (el: HTMLDivElement | null) => {
      sectionRefs.current[id] = el;
    },
    isOpen: openSections[id],
    onOpenChange: (open: boolean) => setOpenSections((prev) => ({ ...prev, [id]: open })),
  });

  useEffect(() => {
    if (!isOpen || !initialSection) return;
    jumpToSection(initialSection);
  }, [isOpen, initialSection, jumpToSection]);

  if (!isOpen) return null;

  return (
    <>
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="fixed inset-0 bg-white dark:bg-gray-900 z-[100] flex flex-col"
      >
        {/* Header + section shortcuts */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 z-10 safe-area-top">
          <div className="p-4 pb-2 flex items-center gap-3">
            <h2 className="text-lg font-semibold flex-1 dark:text-white">Map Layers</h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 -mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Close layer settings"
            >
              <X size={24} />
            </button>
          </div>
          <nav
            className="px-3 pb-3 flex gap-1 overflow-x-auto overscroll-x-contain"
            aria-label="Layer categories"
          >
            {LAYER_SECTION_NAV.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => jumpToSection(id)}
                title={label}
                className={`flex flex-col items-center justify-center gap-0.5 min-w-[3.25rem] px-1.5 py-1.5 rounded-lg border transition-colors shrink-0 ${
                  openSections[id]
                    ? 'border-blue-400/60 bg-blue-50 text-blue-700 dark:border-blue-500/50 dark:bg-blue-950/40 dark:text-blue-200'
                    : 'border-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200'
                }`}
              >
                <Icon size={16} strokeWidth={2} aria-hidden />
                <span className="text-[9px] font-medium leading-none">{label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Unified Layer Stack */}
        <div className="flex-1 overflow-y-auto p-2 overscroll-contain">

          {/* 1. Terrain & Elevation (Base) */}
          <CollapsibleSection title="Terrain & Elevation" defaultOpen={false} {...bindSection('terrain')}>
            {onToggleTerrain && (
              <LayerToggle
                label="3D Terrain"
                enabled={isTerrainEnabled}
                onToggle={() => onToggleTerrain(!isTerrainEnabled)}
                icon={<Mountain size={16} />}
                info="See hills and valleys in 3D. Tilt the map by holding Ctrl + drag (or two-finger drag on mobile) to view terrain."
              />
            )}
            {isTerrainEnabled && onTerrainExaggerationChange && (
              <div className="py-2 px-2 ml-6">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600 dark:text-gray-400">Exaggeration</span>
                  <span className="text-xs font-medium text-gray-900 dark:text-gray-100">{terrainExaggeration.toFixed(1)}x</span>
                </div>
                <input
                  type="range"
                  min="0.5"
                  max="10"
                  step="0.5"
                  value={terrainExaggeration}
                  onChange={(e) => onTerrainExaggerationChange(parseFloat(e.target.value))}
                  className="w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer"
                />
              </div>
            )}
            <LayerToggle
              label="Hillshade"
              enabled={layers.hillshade}
              onToggle={() => onToggleLayer('hillshade', !layers.hillshade)}
              icon={<Mountain size={16} />}
              zoomRange="z8-14"
              info="Adds shading to show hills and slopes more clearly"
            />
            <LayerToggle
              label="Contour lines"
              enabled={layers.contours}
              onToggle={() => onToggleLayer('contours', !layers.contours)}
              icon={<Mountain size={16} />}
              zoomRange="z10-14"
              info="Lines showing elevation levels on the map"
            />
          </CollapsibleSection>

          {/* 2. Administrative Boundaries */}
          <CollapsibleSection title="Administrative Boundaries" defaultOpen={false} {...bindSection('admin')}>
            <LayerToggle
              label="Country boundary"
              enabled={layers.country}
              onToggle={() => onToggleLayer('country', !layers.country)}
              icon={<MapIcon size={16} />}
              zoomRange="z0-14"
              info="Shows Ghana's national border"
            />
            <LayerToggle
              label="Regions"
              enabled={layers.regions}
              onToggle={() => onToggleLayer('regions', !layers.regions)}
              icon={<MapIcon size={16} />}
              zoomRange="z0-14"
              info="Shows the 16 regions of Ghana"
            />
            <LayerToggle
              label="Districts"
              enabled={layers.districts}
              onToggle={() => onToggleLayer('districts', !layers.districts)}
              icon={<MapIcon size={16} />}
              zoomRange="z6-14"
              info="Shows all 261 districts in Ghana"
            />
            <LayerToggle
              label="Service grid"
              enabled={layers.serviceAreas}
              onToggle={() => onToggleLayer('serviceAreas', !layers.serviceAreas)}
              icon={<Grid3X3 size={16} />}
              zoomRange="z8-14"
              info="Grid overlay for postcode service areas"
            />
          </CollapsibleSection>

          {/* Land cover & use — toggle to debug yellow/green fills */}
          <CollapsibleSection title="Land Cover & Use" defaultOpen={false} {...bindSection('land')}>
            <LayerToggle
              label="Land use"
              enabled={layers.landuse}
              onToggle={() => onToggleLayer('landuse', !layers.landuse)}
              icon={<Home size={16} />}
              zoomRange="z5-21"
              info="Residential, commercial, farmland, parks — pale yellow/green fills through building zoom (native tiles z5–21)"
            />
            <LayerToggle
              label="National parks / greenspace"
              enabled={layers.nationalParks}
              onToggle={() => onToggleLayer('nationalParks', !layers.nationalParks)}
              icon={<Mountain size={16} />}
              zoomRange="z5-21"
              info="Named parks & village greens — stays visible under buildings through z21"
            />
            <LayerToggle
              label="Land cover"
              enabled={layers.landcover}
              onToggle={() => onToggleLayer('landcover', !layers.landcover)}
              icon={<Mountain size={16} />}
              zoomRange="z0-21"
              info="Wood, scrub, wetland (OSM landcover) — per-feature tiles; style fades in from z8"
            />
            <LayerToggle
              label="Water bodies"
              enabled={layers.water}
              onToggle={() => onToggleLayer('water', !layers.water)}
              icon={<Ship size={16} />}
              zoomRange="z6-20"
              info="Lakes, reservoirs, and ponds (Lake Volta, etc.)"
            />
            <LayerToggle
              label="Waterways"
              enabled={layers.waterways}
              onToggle={() => onToggleLayer('waterways', !layers.waterways)}
              icon={<Ship size={16} />}
              zoomRange="z6-20"
              info="Rivers, streams, and canals"
            />
            <LayerToggle
              label="Structures"
              enabled={layers.structures}
              onToggle={() => onToggleLayer('structures', !layers.structures)}
              icon={<Construction size={16} />}
              zoomRange="z12-22"
              info="Gates, fences, hedges, and walls. Lines from z16; gate icons from z12 (same as POIs)."
            />
            <LayerToggle
              label="Trees"
              enabled={layers.trees}
              onToggle={() => onToggleLayer('trees', !layers.trees)}
              icon={<TreePine size={16} />}
              zoomRange="z15-22"
              info="Individual mapped trees with Coordal icons and 3D models when zoomed in."
            />
          </CollapsibleSection>

          {/* Weather & climate overlays */}
          <CollapsibleSection title="Weather & Climate" defaultOpen={false} badge="New" {...bindSection('weather')}>
            <p className="mx-2 mb-2 text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
              Historical rainfall, temperature, and wind patterns to help you plan ahead. These layers show past averages — not live weather or flood alerts.
            </p>

            <CollapsibleSection title="Seasonal Flood Exposure" defaultOpen={false} nested>
              <p className="mx-2 mb-2 text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
                {FLOOD_EXPOSURE_SUMMARY}
              </p>
              <LayerToggle
                label="Exposure grid"
                enabled={layers.floodExposure}
                onToggle={() => onToggleLayer('floodExposure', !layers.floodExposure)}
                icon={<CloudRain size={16} />}
                zoomRange={`z${FLOOD_TILE_MIN_ZOOM}-${FLOOD_LAYER_MAX_ZOOM}`}
                info="Colour-coded map squares show low to extreme flood risk based on long-term rainfall. For planning only — not live warnings."
              />
              <CategoricalLegendPill
                title="Flood exposure"
                items={FLOOD_EXPOSURE_GRID_LEGEND.map(({ label, color }) => ({ label, color }))}
                variant="panel"
              />
              <FloodExposureYearPills
                selectedYears={layers.floodExposureYears}
                onToggleFloodYear={onToggleFloodYear}
              />
              <div className="mx-2 mb-2 px-2.5 py-2 rounded-lg bg-emerald-50/80 dark:bg-emerald-900/20 border border-emerald-200/60 dark:border-emerald-800/40">
                <p className="text-[10px] text-emerald-800/90 dark:text-emerald-200/90 leading-relaxed">
                  Turns on automatically while viewing directions. Also shown on street search and route summaries.
                </p>
              </div>
            </CollapsibleSection>

            {weatherLocationUnavailableMessage ? (
              <div className="mx-2 mb-2">
                <WeatherConditionsWarning message={weatherLocationUnavailableMessage} />
              </div>
            ) : (
              OBSERVED_WEATHER_UI_DEFS.map((def) => (
                <ObservedWeatherVariableSection
                  key={def.settingsKey}
                  def={def}
                  enabled={layers[def.settingsKey]}
                  selectedPeriods={layers[def.periodSettingsKey]}
                  onToggleLayer={onToggleLayer}
                  onObservedWeatherPeriodChange={onObservedWeatherPeriodChange}
                  nested
                />
              ))
            )}
          </CollapsibleSection>

          {/* 7-day forecast map layers */}
          <CollapsibleSection title="Weather Forecast" defaultOpen={false} badge="New" {...bindSection('forecast')}>
            <p className="mx-2 mb-2 text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
              See the next seven days on the map. Pick a day below, or open the weather dashboard for a summary at your location.
            </p>
            {onOpenWeatherView && (
              <button
                type="button"
                onClick={onOpenWeatherView}
                className="mx-2 mb-2 w-[calc(100%-1rem)] rounded-lg bg-sky-600 px-3 py-2 text-[12px] font-medium text-white hover:bg-sky-700 dark:bg-sky-700 dark:hover:bg-sky-600"
              >
                Open weather dashboard
              </button>
            )}
            {FORECAST_WEATHER_LAYER_DEFS.map((def) => (
              <ForecastWeatherVariableSection
                key={def.settingsKey}
                def={def}
                enabled={layers[def.settingsKey]}
                selectedPeriods={layers[def.periodSettingsKey]}
                onToggleLayer={onToggleLayer}
                onForecastWeatherPeriodChange={onForecastWeatherPeriodChange}
                nested
              />
            ))}
          </CollapsibleSection>

          {/* 4. Roads & Transit */}
          <CollapsibleSection title="Roads & Transit" defaultOpen={false} {...bindSection('roads')}>
            <LayerToggle
              label="All roads"
              enabled={layers.roads}
              onToggle={() => onToggleLayer('roads', !layers.roads)}
              icon={<Car size={16} />}
              zoomRange="z5-22"
              info="Show or hide all roads on the map"
            />
            <LayerToggle
              label="Pedestrian roads"
              enabled={layers.pedestrianPaths}
              onToggle={() => onToggleLayer('pedestrianPaths', !layers.pedestrianPaths)}
              icon={<Footprints size={16} />}
              zoomRange="z14-22"
              info="Shows walkways and pedestrian paths"
            />
            <LayerToggle
              label="Ferry routes"
              enabled={layers.ferryRoutes}
              onToggle={() => onToggleLayer('ferryRoutes', !layers.ferryRoutes)}
              icon={<Ship size={16} />}
              zoomRange="z8-22"
              info="Shows ferry and boat routes across water"
            />
            <LayerToggle
              label="Transport stops"
              enabled={layers.transportStops}
              onToggle={() => onToggleLayer('transportStops', !layers.transportStops)}
              icon={<MapPin size={16} />}
              zoomRange="z8-22"
              info="Bus, taxi, and rail stops from verified Ghana transport tiles. Icons appear from zoom 8 in urban areas."
            />
            <LayerToggle
              label="Traffic signals"
              enabled={layers.trafficSignals}
              onToggle={() => onToggleLayer('trafficSignals', !layers.trafficSignals)}
              icon={<Car size={16} />}
              zoomRange="z14-22"
              info="Traffic lights and speed cameras from OpenStreetMap, rendered with Coordal SVG icons."
            />
            <LayerToggle
              label="Live traffic"
              enabled={layers.liveTraffic}
              onToggle={() => onToggleLayer('liveTraffic', !layers.liveTraffic)}
              icon={<Car size={16} />}
              zoomRange="z10-22"
              info="Real-time congestion on roads, aligned to Coordal road segments. Refresh via feed_traffic_refresh job."
            />
            <div className="ml-6 mb-2 grid grid-cols-2 gap-x-3 gap-y-1">
              {([
                { color: '#22c55e', label: 'Low' },
                { color: '#eab308', label: 'Moderate' },
                { color: '#f97316', label: 'Heavy' },
                { color: '#ef4444', label: 'Severe' },
              ] as const).map(({ color, label }) => (
                <div key={label} className="flex items-center gap-1.5">
                  <span className="flex-shrink-0 w-4 h-1.5 rounded-full" style={{ backgroundColor: color }} />
                  <span className="text-[11px] text-gray-500 dark:text-neutral-400">{label}</span>
                </div>
              ))}
            </div>
            <LayerToggle
              label="Road quality overlay"
              enabled={layers.roadQuality}
              onToggle={() => onToggleLayer('roadQuality', !layers.roadQuality)}
              icon={<Car size={16} />}
              zoomRange="z10-22"
              info="Colour-codes roads by surface condition. Only roads with a recorded quality rating are shown."
            />
            <div className="ml-6 mb-1 grid grid-cols-2 gap-x-3 gap-y-1">
              {([
                { color: '#06b6d4', label: 'Excellent' },
                { color: '#3b82f6', label: 'Good' },
                { color: '#8b5cf6', label: 'Fair' },
                { color: '#d946ef', label: 'Bad' },
                { color: '#be123c', label: 'Very bad' },
                { color: '#500724', label: 'Impassable' },
              ] as const).map(({ color, label }) => (
                <div key={label} className="flex items-center gap-1.5">
                  <span className="flex-shrink-0 w-4 h-1.5 rounded-full" style={{ backgroundColor: color }} />
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">{label}</span>
                </div>
              ))}
            </div>
            <LayerToggle
              label="Road labels"
              enabled={layers.roadLabels}
              onToggle={() => onToggleLayer('roadLabels', !layers.roadLabels)}
              icon={<Signpost size={16} />}
              zoomRange="z10-22"
              info="Shows street and road names"
            />
          </CollapsibleSection>

          {/* 5. Buildings */}
          <CollapsibleSection title="Buildings" defaultOpen={false} {...bindSection('buildings')}>
            <LayerToggle
              label="Building footprints"
              enabled={layers.buildings}
              onToggle={() => onToggleLayer('buildings', !layers.buildings)}
              icon={<Building2 size={16} />}
              zoomRange="z15-22"
              info="Shows building shapes on the map — turn off to hide all buildings (2D and 3D)"
            />
            <LayerToggle
              label="3D Buildings"
              enabled={layers.buildings3D}
              onToggle={() => onToggleLayer('buildings3D', !layers.buildings3D)}
              icon={<Box size={16} />}
              zoomRange="z15-22"
              info="Shows buildings as 3D shapes with height"
            />
            <LayerToggle
              label="Building labels"
              enabled={layers.buildingLabels}
              onToggle={() => onToggleLayer('buildingLabels', !layers.buildingLabels)}
              icon={<Tag size={16} />}
              zoomRange="z17-22"
              info="Shows house numbers on buildings"
            />
          </CollapsibleSection>

          {/* 6. Places */}
          <CollapsibleSection title="Places" defaultOpen={false} {...bindSection('places')}>
            <LayerToggle
              label="Place labels"
              enabled={layers.placeLabels}
              onToggle={() => onToggleLayer('placeLabels', !layers.placeLabels)}
              icon={<MapPin size={16} />}
              zoomRange="z1-16"
              info="City, region, and district names plus natural feature labels"
            />
            <LayerToggle
              label="Settlement labels"
              enabled={layers.settlementLabels}
              onToggle={() => onToggleLayer('settlementLabels', !layers.settlementLabels)}
              icon={<Home size={16} />}
              zoomRange="z4-16"
              info="Town and village names"
            />
          </CollapsibleSection>

          {/* 7. Points of interest */}
          <CollapsibleSection title="Points of Interest" defaultOpen={false} {...bindSection('poi')}>
            <LayerToggle
              label="POI labels"
              enabled={layers.poiLabels}
              onToggle={() => onToggleLayer('poiLabels', !layers.poiLabels)}
              icon={<Store size={16} />}
              zoomRange="z12-22"
              info="Shops, restaurants, landmarks, and other point-of-interest names"
            />
          </CollapsibleSection>

          {/* Bottom padding */}
          <div className="h-2" />
        </div>

        {/* Footer */}
        <div className="px-3 py-3 border-t border-gray-100 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-900/80">
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
            Toggle layers to customize your map view
          </p>
        </div>
      </motion.div>
    </>
  );
}
